import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { butcherSubscriptionsStatus, errorMessage } from '@helpers/Constants';
import { DollarIcon } from '@helpers/Constants/Icons';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { Subscription } from '@helpers/Models';
import { useEffect, useState } from 'react';

export const CancelSubscription: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { showToastMessage } = useToast();
  const [feedback, setFeedback] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [subscriptionDetails, setSubscriptionDetails] =
    useState<Subscription | null>(null);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  /**
   * Cancels the current subscription.
   *
   * @async
   * @function cancelSubscription
   * @returns {Promise<void>}
   *
   * @remarks
   * - Sends a cancellation request to the backend along with user feedback.
   * - On success:
   *   - Displays a success toast message.
   *   - Updates the subscription status in `localStorage` to CANCELLED.
   *   - Closes the modal or dialog.
   *   - Reloads the page to reflect the updated subscription state.
   * - On failure:
   *   - Displays an error toast message.
   * - Manages loading state during the API call.
   */
  const cancelSubscription = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await Axios.post(ApiEndpoints.CANCEL_SUBSCRIPTION, {
        feedback: feedback,
      });
      showToastMessage(response.data.message, 'success');
      // Set the subscription status to cancelled (5)
      localStorage.setItem(
        'subscriptionStatus',
        butcherSubscriptionsStatus.CANCELLED.toString(),
      );
      // Set the subscriptionActiveFlag status to 0 to restrict the users without
      // an active plan
      localStorage.setItem('subscriptionActiveFlag', '0');
      onClose();
      // Do reload to get the latest subscription status reflected
      window.location.reload();
    } catch {
      showToastMessage(errorMessage.ERROR_CANCELLING_SUBSCRIPTION, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async () => {
    try {
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      setSubscriptionDetails(response.data.data.subscription);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-lg shadow-lg p-3">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white flex justify-between items-center px-2 py-4 border-b">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold">Cancel Subscription</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#991B1B] font-bold text-xl leading-none"
          >
            ✕
          </button>
        </div>
        {/* Main Content */}
        <div className="p-3 overflow-y-auto">
          <div className="bg-[#FEF2F2] text-[#991B1B] mt-2 p-3 rounded-md">
            <div className="flex flex-row mb-2">
              {/* Info Icon */}
              <span className="mt-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 15H11V9H9V15ZM10.7125 6.7125C10.9042 6.52083 11 6.28333 11 6C11 5.71667 10.9042 5.47917 10.7125 5.2875C10.5208 5.09583 10.2833 5 10 5C9.71667 5 9.47917 5.09583 9.2875 5.2875C9.09583 5.47917 9 5.71667 9 6C9 6.28333 9.09583 6.52083 9.2875 6.7125C9.47917 6.90417 9.71667 7 10 7C10.2833 7 10.5208 6.90417 10.7125 6.7125ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18Z"
                    fill="#991B1B"
                  />
                </svg>
              </span>
              <span className="ml-3 uppercase font-bold">Important Notice</span>
            </div>
            <span className="ml-2">
              If you cancel your subscription, your data will be retained for
              one year. You can reactivate your subscription at any time during
              this period. Any outstanding invoice amount will be charged to
              your credit card.
            </span>
          </div>

          {/* Feedback */}
          <div className="mt-6 flex flex-col">
            <span className="text-[#374151]">
              Help us improve - Why are you canceling? *
            </span>
            {/* Textarea */}
            <textarea
              name="packagingNotes"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="The features don't match what I'm looking for..."
              className="w-full mt-3 border rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows={4}
            />
            <span className="mt-3 text-sm text-[#6B7280]">
              Your feedback is valuable to us
            </span>
          </div>

          {/* Outstanding Balance Card*/}
          <div className="border border-[#E8E8E8] p-4 mt-5 rounded-md">
            <div className="flex flex-row mb-5">
              {/* Info Icon */}
              <span className="mt-1">
                <DollarIcon />
              </span>
              <span className="ml-3 text-lg font-bold">
                Outstanding Balance
              </span>
            </div>
            <div className="flex justify-between bg-[#F8F8F8] rounded-md px-3 py-5 mb-4">
              <span className="text-[#000A16] font-medium">
                Amount to be charged immediately:
              </span>
              <span className="text-[#000A16] font-bold text-xl">
                ${subscriptionDetails?.estimatedCost}
              </span>
            </div>

            <span className="text-[#000A16] text-sm mt-4">
              Since you are canceling mid-subscription, the outstanding invoice
              amount for this billing period (
              {subscriptionDetails?.orderSpeciesCount} animals × $
              {subscriptionDetails?.price}) will be charged to your credit card
              immediately upon confirmation. You will receive an email
              confirmation of this charge.
            </span>
          </div>

          <div className="bg-[#F0FDF4] rounded-md flex flex-row gap-3 p-4 mt-4 items-center justify-center w-full">
            <span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 16C5.76667 16 3.875 15.225 2.325 13.675C0.775 12.125 0 10.2333 0 8C0 5.76667 0.775 3.875 2.325 2.325C3.875 0.775 5.76667 0 8 0C9.15 0 10.25 0.2375 11.3 0.7125C12.35 1.1875 13.25 1.86667 14 2.75V0H16V7H9V5H13.2C12.6667 4.06667 11.9375 3.33333 11.0125 2.8C10.0875 2.26667 9.08333 2 8 2C6.33333 2 4.91667 2.58333 3.75 3.75C2.58333 4.91667 2 6.33333 2 8C2 9.66667 2.58333 11.0833 3.75 12.25C4.91667 13.4167 6.33333 14 8 14C9.28333 14 10.4417 13.6333 11.475 12.9C12.5083 12.1667 13.2333 11.2 13.65 10H15.75C15.2833 11.7667 14.3333 13.2083 12.9 14.325C11.4667 15.4417 9.83333 16 8 16Z"
                  fill="#166534"
                />
              </svg>
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-[#166534] font-medium">
                You can reactivate your subscription anytime within one year.
                Your data will be retained during this period.
              </span>
            </div>
          </div>
          {/* Footer buttons */}
          <div className="flex justify-between gap-4 w-full mt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="w-[50%] px-6 py-2 border rounded-md font-semibold bg-white text-primary border-primary focus:outline-none focus:ring-2"
            >
              Go Back
            </button>
            <button
              onClick={cancelSubscription}
              type="button"
              className="w-[50%] px-6 py-2 border rounded-md font-bold bg-white text-[#DF1A0C] border-[#DF1A0C] focus:outline-none focus:ring-2"
            >
              {isLoading ? 'Canceling...' : 'Confirm Cancellation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
